/**
 * Schema grouping utilities
 * Extracts tab and section metadata from schema and handles x-pages filtering
 */

import type { RJSFSchema, UiSchema } from '@rjsf/utils';

/**
 * Configuration for a tab in the form editor
 */
export interface TabConfig {
    id: string;
    title: string;
    'x-pages'?: string | string[];
}

/**
 * Configuration for a section within a tab
 */
export interface SectionConfig {
    id: string;
    title: string;
    tabId?: string; // Optional: if section belongs to a specific tab
    'x-pages'?: string | string[];
}

/**
 * Field grouping metadata indicating which tab and section a field belongs to
 */
interface FieldGrouping {
    tabId?: string;
    sectionId?: string;
    path: string[]; // Full path to the field (e.g., ['content', 'app'])
}

/**
 * Complete schema grouping structure with tabs, sections, and field assignments
 */
export interface SchemaGrouping {
    tabs: TabConfig[];
    sections: SectionConfig[];
    fieldGrouping: Record<string, FieldGrouping>; // Key is dot-joined path (e.g., 'content.app')
}

/**
 * Element that can have x-pages filtering (tab or section)
 */
interface ElementWithXPages {
    id: string;
    'x-pages'?: string | string[];
}

/**
 * JSON Schema type (compatible with RJSFSchema)
 * Uses RJSFSchema directly for full compatibility
 */
type JsonSchema = RJSFSchema;

/**
 * UI Schema type - use RJSF's UiSchema directly for full compatibility
 * Extended with our custom properties for grouping and x-pages
 */
type ExtendedUiSchema = UiSchema & {
    'ui:grouping'?: {
        tabs?: TabConfig[];
        sections?: SectionConfig[];
    };
    'x-pages'?: string | string[];
};

/**
 * Check if a page path matches a pattern (supports exact match and parameterized paths)
 * @example
 * - matchesPagePath('/checkout', '/checkout') => true
 * - matchesPagePath('/thankyou/123', '/thankyou/:orderId') => true
 * - matchesPagePath('/checkout', '/offer') => false
 */
function matchesPagePath(currentPagePath: string, pattern: string): boolean {
    // Exact match
    if (pattern === currentPagePath) {
        return true;
    }

    // Parameterized path matching (e.g., /thankyou/:orderId matches /thankyou/123)
    if (pattern.includes(':')) {
        const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(currentPagePath);
    }

    return false;
}

/**
 * Check if any of the x-pages patterns match the current page path
 */
function matchesAnyPagePath(currentPagePath: string, xPages: string | string[]): boolean {
    const pages = Array.isArray(xPages) ? xPages : [xPages];
    return pages.some((page) => matchesPagePath(currentPagePath, page));
}

/**
 * Infer x-pages from element ID if it matches an available page path
 * @example
 * - inferXPagesFromId('club', ['/club', '/checkout']) => ['/club']
 * - inferXPagesFromId('settings', ['/club', '/checkout']) => undefined
 */
function inferXPagesFromId(
    elementId: string,
    availablePagePaths?: string[],
): string[] | undefined {
    if (!availablePagePaths || availablePagePaths.length === 0) {
        return undefined;
    }

    const inferredPath = `/${elementId}`;
    const matches = availablePagePaths.some(
        (path) => path === inferredPath || path.startsWith(`${inferredPath}/`),
    );

    return matches ? [inferredPath] : undefined;
}

/**
 * Get effective x-pages for an element (explicit or inferred)
 */
function getEffectiveXPages(
    element: ElementWithXPages,
    availablePagePaths?: string[],
): string[] | undefined {
    // Use explicit x-pages if available
    if (element['x-pages']) {
        return Array.isArray(element['x-pages']) ? element['x-pages'] : [element['x-pages']];
    }

    // Try to infer from element ID
    return inferXPagesFromId(element.id, availablePagePaths);
}

/**
 * Check if a field should be included based on x-pages filter
 * @param fieldUiSchema - UI schema for the field (may contain x-pages)
 * @param currentPagePath - Current active page path (e.g., '/checkout', '/offer')
 * @returns true if field should be included, false otherwise
 */
function shouldIncludeField(fieldUiSchema: ExtendedUiSchema, currentPagePath?: string): boolean {
    // If no x-pages filter, include the field
    if (!fieldUiSchema['x-pages']) {
        return true;
    }

    // If no current page path, exclude fields with x-pages filter
    if (!currentPagePath) {
        return false;
    }

    return matchesAnyPagePath(currentPagePath, fieldUiSchema['x-pages']);
}

/**
 * Recursively extract field assignments from nested object properties
 * Filters fields based on x-pages and extracts tab/section assignments
 * @param schema - JSON schema object
 * @param uiSchema - UI schema object
 * @param parentPath - Path to parent field (for nested fields)
 * @param parentTabId - Tab ID inherited from parent
 * @param currentPagePath - Current active page path for x-pages filtering
 * @returns Map of field paths to their grouping metadata
 */
function extractNestedFieldGroupings(
    schema: JsonSchema,
    uiSchema: ExtendedUiSchema,
    parentPath: string[] = [],
    parentTabId?: string,
    currentPagePath?: string,
): Record<string, FieldGrouping> {
    const fieldGrouping: Record<string, FieldGrouping> = {};

    if (!schema || !schema.properties) {
        return fieldGrouping;
    }

    Object.keys(schema.properties).forEach((fieldName) => {
        const currentPath = [...parentPath, fieldName];
        const pathKey = currentPath.join('.');
        const fieldSchemaDef = schema.properties![fieldName];
        const fieldUiSchema = (uiSchema[fieldName] || {}) as UiSchema;

        // Skip if fieldSchema is a boolean (JSON Schema shorthand: true = accept, false = reject)
        if (typeof fieldSchemaDef === 'boolean') {
            return;
        }

        const fieldSchema = fieldSchemaDef as JsonSchema;

        // Filter by x-pages if specified
        if (!shouldIncludeField(fieldUiSchema, currentPagePath)) {
            return; // Skip this field
        }

        const grouping: FieldGrouping = {
            path: currentPath,
        };

        // Inherit tab from parent if not explicitly set
        if (fieldUiSchema['ui:tab']) {
            grouping.tabId = fieldUiSchema['ui:tab'];
        } else if (parentTabId) {
            grouping.tabId = parentTabId;
        }

        // Check for section assignment
        if (fieldUiSchema['ui:section']) {
            grouping.sectionId = fieldUiSchema['ui:section'];
        }

        // If this field has tab or section assignment, add it
        if (grouping.tabId || grouping.sectionId) {
            fieldGrouping[pathKey] = grouping;
        }

        // If this is an object with nested properties, recurse
        const isObjectType =
            fieldSchema.type === 'object' ||
            (Array.isArray(fieldSchema.type) && fieldSchema.type.includes('object'));
        if (
            fieldSchema &&
            typeof fieldSchema === 'object' &&
            isObjectType &&
            fieldSchema.properties
        ) {
            // Check if it's a translation field - don't recurse into those
            const isTranslationField = fieldSchema.format === 'translation' || fieldUiSchema['ui:field'] === 'translation';

            if (!isTranslationField) {
                // Recurse into nested properties
                const nestedGroupings = extractNestedFieldGroupings(
                    fieldSchema,
                    fieldUiSchema,
                    currentPath,
                    grouping.tabId || parentTabId,
                    currentPagePath,
                );
                Object.assign(fieldGrouping, nestedGroupings);
            }
        }
    });

    return fieldGrouping;
}

/**
 * Check if a section/tab should be included based on x-pages filter
 */
function shouldIncludeSectionByXPages(
    element: ElementWithXPages,
    currentPagePath?: string,
    availablePagePaths?: string[],
): boolean {
    const effectiveXPages = getEffectiveXPages(element, availablePagePaths);

    // If no x-pages filter (explicit or inferred), include the section/tab
    if (!effectiveXPages) {
        return true;
    }

    // If no current page path, exclude sections/tabs with x-pages filter
    if (!currentPagePath) {
        return false;
    }

    return matchesAnyPagePath(currentPagePath, effectiveXPages);
}

/**
 * Build a map of tab IDs to their allowed page paths (from sections with x-pages)
 * @param sections - All sections from the schema grouping
 * @param availablePagePaths - Available page paths for inference
 * @returns Map of tab ID to set of allowed page paths
 */
function buildTabXPagesMap(
    sections: SectionConfig[],
    availablePagePaths?: string[],
): Map<string, Set<string>> {
    const tabXPagesMap = new Map<string, Set<string>>();

    sections.forEach((section) => {
        if (!section.tabId) return;

        const effectiveXPages = getEffectiveXPages(section, availablePagePaths);
        if (!effectiveXPages) return;

        const tabId = section.tabId;
        if (!tabXPagesMap.has(tabId)) {
            tabXPagesMap.set(tabId, new Set());
        }

        effectiveXPages.forEach((page) => tabXPagesMap.get(tabId)!.add(page));
    });

    return tabXPagesMap;
}

/**
 * Count sections without x-pages per tab
 * @param sections - All sections from the schema grouping
 * @returns Map of tab ID to count of sections without x-pages
 */
function countSectionsWithoutXPages(sections: SectionConfig[]): Map<string, number> {
    const counts = new Map<string, number>();

    sections.forEach((section) => {
        if (section.tabId && !section['x-pages']) {
            counts.set(section.tabId, (counts.get(section.tabId) || 0) + 1);
        }
    });

    return counts;
}

/**
 * Check if a tab should be visible based on its sections' x-pages restrictions
 * @param tab - Tab configuration
 * @param tabXPagesMap - Map of tab IDs to allowed page paths
 * @param sectionsWithoutXPagesCount - Map of tab IDs to count of sections without x-pages
 * @param currentPagePath - Current active page path
 * @returns true if tab should be visible, false otherwise
 */
function shouldIncludeTabBySections(
    tab: TabConfig,
    tabXPagesMap: Map<string, Set<string>>,
    sectionsWithoutXPagesCount: Map<string, number>,
    currentPagePath?: string,
): boolean {
    const hasXPagesSections = tabXPagesMap.has(tab.id);
    const hasSectionsWithoutXPages = (sectionsWithoutXPagesCount.get(tab.id) || 0) > 0;

    // If all sections have x-pages restrictions, tab should only show when current page matches
    if (hasXPagesSections && !hasSectionsWithoutXPages && currentPagePath) {
        const allowedPages = tabXPagesMap.get(tab.id)!;
        return Array.from(allowedPages).some((page) => matchesPagePath(currentPagePath, page));
    }

    // Otherwise, tab visibility is determined by visible content
    return true;
}

/**
 * Filter tabs and sections based on x-pages (with inference)
 * @param allTabs - All tabs from schema grouping
 * @param allSections - All sections from schema grouping
 * @param currentPagePath - Current active page path
 * @param availablePagePaths - Available page paths for x-pages inference
 * @returns Filtered tabs and sections
 */
function filterTabsAndSections(
    allTabs: TabConfig[],
    allSections: SectionConfig[],
    currentPagePath?: string,
    availablePagePaths?: string[],
): { tabs: TabConfig[]; sections: SectionConfig[] } {
    const tabs = allTabs.filter((tab) =>
        shouldIncludeSectionByXPages(tab, currentPagePath, availablePagePaths),
    );

    const sections = allSections.filter((section) =>
        shouldIncludeSectionByXPages(section, currentPagePath, availablePagePaths),
    );

    return { tabs, sections };
}

/**
 * Parse schema grouping configuration from uiSchema
 * Extracts tabs, sections, and field assignments (including nested fields)
 * Filters elements based on x-pages metadata
 *
 * @param schema - JSON schema object
 * @param uiSchema - UI schema object with grouping metadata
 * @param currentPagePath - Current active page path for x-pages filtering (e.g., '/checkout')
 * @param availablePagePaths - Available page paths for x-pages inference (e.g., ['/checkout', '/offer', '/club'])
 * @returns Parsed and filtered schema grouping
 *
 * @example
 * ```typescript
 * const grouping = parseSchemaGrouping(
 *   schema,
 *   uiSchema,
 *   '/checkout',
 *   ['/checkout', '/offer', '/club']
 * );
 * // Returns only tabs/sections/fields visible on '/checkout' page
 * ```
 */
export function parseSchemaGrouping(
    schema: JsonSchema,
    uiSchema: ExtendedUiSchema,
    currentPagePath?: string,
    availablePagePaths?: string[],
): SchemaGrouping {
    const grouping: SchemaGrouping = {
        tabs: [],
        sections: [],
        fieldGrouping: {},
    };

    // Get grouping metadata from uiSchema root
    const rootGrouping = uiSchema['ui:grouping'] || {};
    const allTabs = rootGrouping.tabs || [];
    const allSections = rootGrouping.sections || [];

    // Filter tabs and sections based on x-pages (with inference)
    const { tabs: filteredTabs, sections: filteredSections } = filterTabsAndSections(
        allTabs,
        allSections,
        currentPagePath,
        availablePagePaths,
    );

    grouping.tabs = filteredTabs;
    grouping.sections = filteredSections;

    // Extract field assignments from properties (including nested)
    if (schema.properties) {
        grouping.fieldGrouping = extractNestedFieldGroupings(
            schema,
            uiSchema,
            [],
            undefined,
            currentPagePath,
        );
    }

    // Calculate visible content to determine final tab visibility
    const visibleTabIds = new Set<string>();

    // Add tabs that have visible sections
    grouping.sections.forEach((section) => {
        if (section.tabId) {
            visibleTabIds.add(section.tabId);
        }
    });

    // Add tabs that have visible fields
    Object.values(grouping.fieldGrouping).forEach((fieldGroup) => {
        if (fieldGroup.tabId) {
            visibleTabIds.add(fieldGroup.tabId);
        }
    });

    // Build maps for tab filtering logic
    const tabXPagesMap = buildTabXPagesMap(allSections, availablePagePaths);
    const sectionsWithoutXPagesCount = countSectionsWithoutXPages(allSections);

    // Final tab filtering: include tabs with visible content or matching x-pages restrictions
    grouping.tabs = grouping.tabs.filter((tab) => {
        // If tab has explicit x-pages, use that filter (already filtered above)
        if (tab['x-pages']) {
            return true; // Already filtered by filterTabsAndSections
        }

        // Check if tab should be visible based on sections' x-pages restrictions
        const shouldIncludeBySections = shouldIncludeTabBySections(
            tab,
            tabXPagesMap,
            sectionsWithoutXPagesCount,
            currentPagePath,
        );

        if (!shouldIncludeBySections) {
            return false;
        }

        // Tab should be visible if it has any visible content
        return visibleTabIds.has(tab.id);
    });

    return grouping;
}

/**
 * Get nested schema value by path
 * @param schema - JSON schema object
 * @param path - Array of keys representing the path (e.g., ['content', 'app'])
 * @returns Schema at the specified path, or null if not found
 */
function getNestedSchema(schema: JsonSchema, path: string[]): JsonSchema | null {
    let current: JsonSchema = schema;
    for (const key of path) {
        if (current && current.properties && current.properties[key]) {
            const prop = current.properties[key];
            // Handle boolean values (JSON Schema shorthand)
            if (typeof prop === 'boolean') {
                return null; // Boolean schemas don't have nested structure
            }
            current = prop as JsonSchema;
        } else {
            return null;
        }
    }
    return current;
}

/**
 * Get nested uiSchema value by path
 * @param uiSchema - UI schema object
 * @param path - Array of keys representing the path (e.g., ['content', 'app'])
 * @returns UI schema at the specified path, or empty object if not found
 */
function getNestedUiSchema(uiSchema: ExtendedUiSchema, path: string[]): ExtendedUiSchema {
    let current: ExtendedUiSchema = uiSchema;
    for (const key of path) {
        if (current && current[key]) {
            current = current[key] as ExtendedUiSchema;
        } else {
            return {} as unknown as ExtendedUiSchema;
        }
    }
    return (current || ({} as unknown as ExtendedUiSchema)) as ExtendedUiSchema;
}

/**
 * Group fields by tab and section (handles nested field paths)
 * Creates a hierarchical structure: tabId -> sectionId -> field paths[]
 *
 * @param schema - JSON schema object (unused but kept for API consistency)
 * @param grouping - Parsed schema grouping with tabs, sections, and field assignments
 * @returns Nested object structure: { [tabId]: { [sectionId]: fieldPath[] } }
 *
 * @example
 * ```typescript
 * {
 *   'settings': {
 *     'branding': ['branding.primaryColor', 'branding.fontFamily'],
 *     'header': ['header.logoUrl']
 *   },
 *   'content': {
 *     'texts': ['texts.title', 'texts.description']
 *   }
 * }
 * ```
 */
export function groupFieldsByTabAndSection(
    schema: JsonSchema,
    grouping: SchemaGrouping,
): Record<string, Record<string, string[]>> {
    const result: Record<string, Record<string, string[]>> = {};

    // Initialize structure
    if (grouping.tabs.length > 0) {
        grouping.tabs.forEach((tab) => {
            result[tab.id] = {};
            // Initialize sections that belong to this tab (explicitly or via fields)
            grouping.sections.forEach((section) => {
                // If section has explicit tabId or fields will assign it
                if (!section.tabId || section.tabId === tab.id) {
                    result[tab.id][section.id] = [];
                }
            });
        });
    } else {
        // No tabs, use default structure
        result['default'] = {};
        grouping.sections.forEach((section) => {
            result['default'][section.id] = [];
        });
    }

    // Assign fields to tabs and sections (including nested fields)
    Object.entries(grouping.fieldGrouping).forEach(([pathKey, fieldGrouping]) => {
        const tabId = fieldGrouping.tabId || 'default';
        const sectionId = fieldGrouping.sectionId || 'default';

        if (!result[tabId]) {
            result[tabId] = {};
        }
        if (!result[tabId][sectionId]) {
            result[tabId][sectionId] = [];
        }
        result[tabId][sectionId].push(pathKey);
    });

    return result;
}

